/**
 * Rich SHELL CSS per skin — shell-side counterpart to module-host/skins/*.css.
 * Appended by buildSkinCss (SSR + client); every rule is scoped under :root[data-skin="<id>"].
 * Authored + iterated against the live app; keep in sync with the module premium look.
 */
export const SHELL_SKIN_CSS: Record<string, string> = {
  premium: `/* ==========================================================================
   PREMIUM SHELL SKIN — synthesized "Refined depth"
   Same design language as the module Home dashboard: navy #1F4E79 primary,
   sky #1BA1E2 secondary, gold #C9A24B used sparingly, cool navy-tinted
   neutrals, soft rounded white cards floating on an airy cool-white canvas,
   crisp deep-navy sidebar rail with a sky→navy active pill + gold left tab.
   Every rule scoped under :root[data-skin="premium"]. Semantic hues
   (success/warning/destructive) keep their natural values.
   ========================================================================== */

/* ---- 0. DESIGN TOKENS ---------------------------------------------------
   LIGHT values, scoped :not(.dark). They used to be emitted under the .dark
   selector too so the mode class could not out-specify them — the effect was
   that dark mode never applied for any skinned org, and premium is the platform
   default. The dark twin follows. */
:root[data-skin="premium"]:not(.dark) {
  /* canvas + ink */
  --background: #eef3f9;
  --foreground: #16283b;
  --card: #ffffff;
  --card-foreground: #16283b;
  --popover: #ffffff;
  --popover-foreground: #16283b;

  /* brand family — navy primary, sky + gold accents */
  --brand: #1f4e79;
  --brand-foreground: #ffffff;
  --brand-muted: color-mix(in oklab, #1f4e79 12%, transparent);
  --primary: #1f4e79;
  --primary-foreground: #ffffff;
  --sky: #1ba1e2;
  --gold: #c9a24b;

  /* semantic — natural hues, kept */
  --success: #2f9e5b;
  --warning: #c7902b;
  --destructive: #d64545;
  /* -ink: text-safe on a WASH of the fill above (badge/status-label case), same
     split as globals.css's own --destructive vs --destructive-ink. Without
     these, the tokens fell through to skinVarDeclarations' GENERIC defaults
     (readableInk against generic success/warning/danger hues, not premium's
     own) — close by coincidence in light mode, but solved against the LIGHT
     --card in BOTH modes (skinVarDeclarations never branches on mode), so dark
     mode inherited a light-solved value on a dark card. Computed here with
     readableInk(fill, card) — 5.53–5.68:1 against #ffffff. */
  --success-ink: #237543;
  --warning-ink: #87621d;
  --destructive-ink: #b83b3b;
  --gold-ink: #7d642f;

  /* refined cool neutrals */
  --secondary: #eaf1f8;
  --secondary-foreground: #1f3350;
  --muted: #edf2f8;
  /* #536174, not #5f7085: fine on a white card (5.07:1) but this token lands
     on tinted panels too — 3.76:1 on #d7dfe6 and 4.28:1 on #dfeef8. Solved
     against the darkest ground it actually meets. */
  --muted-foreground: #536174;
  --accent: #e6eff9;
  --accent-foreground: #1f3350;
  --border: #dce4ee;
  --input: #d3ddea;
  --ring: #1ba1e2;

  /* charts follow the brand set */
  --chart-1: #1f4e79;
  --chart-2: #1ba1e2;
  --chart-3: #c9a24b;
  --chart-4: #2f9e5b;
  --chart-5: #7c5cd6;

  /* generous rounding cascades to sm→4xl */
  --radius: 0.8rem;

  /* SIDEBAR — frosted light rail floating over a brand-hued colour field.
     The rail is LIGHT because the brand mark is a mid-blue logotype (#0070C0):
     against any dark surface it tops out at 4.12:1 even on pure black, so it
     can never read there. On this rail it sits near 6:1. The colour beneath the
     glass is what keeps it from being a plain white rail. */
  --sidebar: #f7fafd;
  --sidebar-foreground: #1b2c3b;
  --sidebar-primary: #1ba1e2;
  --sidebar-primary-foreground: #ffffff;
  /* Solid values, because these are the FLAT defaults.
     They were rgba(255,255,255,.72) and rgba(255,255,255,.85) — frosted
     highlights tuned for the dark mesh the rail carries under GLASS. In flat
     the rail is --sidebar (#f7fafd, near-white), so white-over-near-white
     composites to ~#fdfeff: measured 1.05:1, i.e. the selected nav item and
     every sidebar divider were invisible. The translucent pair is restored
     below, gated on [data-surface="glass"], where it is correct. */
  --sidebar-accent: #e4edf6;
  --sidebar-accent-foreground: #10314f;
  --sidebar-border: #dbe6f1;
  --sidebar-ring: #1ba1e2;

  /* The hand-tuned rail mesh. Declared in BOTH palette blocks with the same
     literals because the original mesh was mode-independent — so premium renders
     exactly as it was tuned, in light and dark. Any other brand has no palette
     block, the fallbacks fire, and the colour field is derived from ITS tokens. */
  --chrome-mesh-1: #2e86c8;
  --chrome-mesh-2: #e0b451;
  --chrome-mesh-3: #c9a24b;
  --chrome-mesh-4: #1ba1e2;
  --chrome-mesh-base: #1e4f78;
}
/* Dark twin. Hues carried from the light palette, lightness/chroma re-derived for
   a dark ground and contrast-verified against --card: foreground 14.4:1,
   muted-foreground 7.7:1, brand 6.3:1, destructive 6.0:1, success 8.2:1,
   warning 8.5:1; sidebar foreground 12.8:1 on --sidebar. */
:root[data-skin="premium"].dark {
  --background: #0e1318;
  --foreground: #e7edf5;
  --card: #161d24;
  --card-foreground: #e7edf5;
  --popover: #1a222b;
  --popover-foreground: #e7edf5;
  /* #65a7dd, not #5fa3dc: as TEXT on the glass panels this token lands on it
     measured 4.46:1, just under threshold. The lift also helps its own fill,
     because --primary-foreground here is dark ink on the blue. */
  --brand: #65a7dd;
  --brand-foreground: #0b1622;
  --brand-muted: color-mix(in oklab, #65a7dd 22%, transparent);
  --primary: #65a7dd;
  --primary-foreground: #0b1622;
  --sky: #4fc0f0;
  --gold: #d8b45f;
  --success: #54c98a;
  --warning: #e0b055;
  --destructive: #f07272;
  /* Dark twin of the -ink block in the light palette above. On this dark ground
     the fills themselves already clear 4.5:1 as text on their own wash (5.95–
     8.59:1 — readableInk(fill, card) returns the fill unchanged), unlike light
     mode where the fills had to be darkened. See docs/dark-glass-audit.md #2. */
  --success-ink: #54c98a;
  --warning-ink: #e0b055;
  --destructive-ink: #f07272;
  --gold-ink: #d8b45f;
  --secondary: #262e39;
  --secondary-foreground: #dae4f0;
  --muted: #252c35;
  --muted-foreground: #9fb0c4;
  --accent: #293340;
  --accent-foreground: #e7edf5;
  --border: rgba(255,255,255,.13);
  --input: rgba(255,255,255,.17);
  --ring: #4fc0f0;
  --chart-1: #5fa3dc;
  --chart-2: #4fc0f0;
  --chart-3: #d8b45f;
  --chart-4: #54c98a;
  --chart-5: #a78bfa;
  --radius: 0.8rem;
  --sidebar: #11181f;
  --sidebar-foreground: #cfdcea;
  --sidebar-primary: #4fc0f0;
  --sidebar-primary-foreground: #0b1622;
  --sidebar-accent: rgba(255,255,255,.10);
  --sidebar-accent-foreground: #ffffff;
  --sidebar-border: rgba(255,255,255,.12);
  --sidebar-ring: #4fc0f0;

  /* The hand-tuned rail mesh. Declared in BOTH palette blocks with the same
     literals because the original mesh was mode-independent — so premium renders
     exactly as it was tuned, in light and dark. Any other brand has no palette
     block, the fallbacks fire, and the colour field is derived from ITS tokens. */
  --chrome-mesh-1: #2e86c8;
  --chrome-mesh-2: #e0b451;
  --chrome-mesh-3: #c9a24b;
  --chrome-mesh-4: #1ba1e2;
  --chrome-mesh-base: #1e4f78;
}


/* ---- SIDEBAR GLASS ------------------------------------------------------
   Two stacked layers inside the rail: a brand-hued mesh, then a frosted pane
   over it. The blur is deliberate rather than decorative — it has the mesh to
   refract, which is the only reason glass reads as glass here. Content sits
   above both.

   Gated on [data-surface="glass"] — the user's own flat/glass toggle (shell
   topbar, qod_surface cookie; see docs/glass-surface-theme.md) — rather than
   firing unconditionally whenever premium is the active skin. It used to
   ignore the toggle entirely: a premium user who picked "flat" still got a
   frosted rail, and every OTHER skin's sidebar never responded to "glass"
   at all (nothing in the generic glass :where() list in globals.css covers
   [data-slot="sidebar"] either — see the audit in docs/dark-glass-audit.md).
   data-surface is always one of "flat"/"glass" on <html> (never absent —
   src/app/layout.tsx stamps it from ssrSurfaceFromCookies), so this is a
   safe, always-resolved qualifier. */
:root[data-skin="premium"] [data-slot="sidebar-inner"] {
  position: relative;
  isolation: isolate;
}
/* Only glass mode needs the rail transparent so the mesh/frost layers below
   show through — flat mode keeps the normal opaque --sidebar background. */
:root[data-skin="premium"][data-surface="glass"] [data-slot="sidebar-inner"] {
  background: transparent;
}
/* The frosted highlight/divider pair belongs to GLASS: over the mesh below,
   white at 72%/85% reads as lit glass. Kept here rather than in the palette
   block so flat gets solid, visible values instead (see the note there). */
:root[data-skin="premium"][data-surface="glass"] {
  --sidebar-accent: rgba(255, 255, 255, 0.72);
  --sidebar-border: rgba(255, 255, 255, 0.85);
}
:root[data-skin="premium"][data-surface="glass"] [data-slot="sidebar-inner"]::before {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 0;
  background:
    /* Premium supplies --chrome-mesh-* from its own palette block, so its rail is
       pixel-identical to the look that was tuned by hand. Any OTHER brand has no
       palette block, so the fallback fires and the mesh is derived from that
       brand's tokens — a purple brand gets a purple colour field. */
    radial-gradient(300px 200px at 0% 0%, var(--chrome-mesh-1, color-mix(in srgb, var(--sky) 70%, var(--brand))) 0%, transparent 62%),
    radial-gradient(460px 420px at 95% 34%, var(--chrome-mesh-2, color-mix(in srgb, var(--gold) 80%, white)) 0%, transparent 68%),
    radial-gradient(340px 300px at 12% 66%, var(--chrome-mesh-3, var(--gold)) 0%, transparent 62%),
    radial-gradient(320px 280px at 70% 100%, var(--chrome-mesh-4, var(--sky)) 0%, transparent 58%),
    var(--chrome-mesh-base, var(--brand));
}
:root[data-skin="premium"][data-surface="glass"] [data-slot="sidebar-inner"]::after {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 1;
  backdrop-filter: blur(30px) saturate(130%);
  /* Derived from the brand's own rail token, NOT a hardcoded near-white.
     Hardcoding it meant this pane painted over sidebarBg for every brand, so a
     brand that chose a DARK rail in light mode got its light sidebarFg on a
     near-white pane: measured 1.17:1, i.e. invisible navigation. Premium has no
     sidebarBg, so --sidebar is its surface2 (#eef2f7) and it lands where it
     always did. */
  background: color-mix(in srgb, var(--sidebar) 88%, transparent);
}

/* The frosted pane, dark. A light pane over the dark mesh would blow out the
   whole rail; this keeps the same blur and flips the tint. */
:root[data-skin="premium"][data-surface="glass"].dark [data-slot="sidebar-inner"]::after {
  background: color-mix(in srgb, var(--sidebar) 82%, transparent);
  border-right-color: rgba(255, 255, 255, 0.10);
}
/* No white line down the rail's right edge. TWO were stacking there: the base
   component puts a border-r on the container (coloured by --sidebar-border,
   which this skin sets to near-white), and the frosted pane drew its own 1px
   white border on top. Against a light canvas that reads as a seam belonging to
   neither surface. Zero the COLOUR rather than the border itself, so the box
   model is untouched and nothing shifts by a pixel; the rail's glass is already
   distinct enough from the content field to separate them. Only needed in glass
   mode — flat mode wants its normal --sidebar-border edge.
   (No backticks in this file: the whole stylesheet is a JS template literal.) */
:root[data-skin="premium"][data-surface="glass"] [data-slot="sidebar-container"] {
  border-right-color: transparent;
}
:root[data-skin="premium"] [data-slot="sidebar-inner"] > * {
  position: relative;
  z-index: 2;
}
/* No backdrop-filter (older Firefox, or the user disabled it): the mesh would
   show through raw and shred the text, so fall back to an opaque light rail. */
@supports not (backdrop-filter: blur(1px)) {
  :root[data-skin="premium"][data-surface="glass"] [data-slot="sidebar-inner"]::before { display: none; }
  :root[data-skin="premium"][data-surface="glass"] [data-slot="sidebar-inner"]::after {
    background: var(--sidebar);
  }
}
/* MOBILE sidebar — a Sheet with data-slot="sidebar" (NOT the desktop
   sidebar-inner), so none of the frosted-light overlay above ever reaches it. It
   came up as a dark navy drawer still wearing the rail's DARK text (#1b2c3b) —
   dark-on-dark, unreadable. A drawer is a modal OVER the content, not a rail
   floating on the mesh, so it wants a solid fill rather than glass: the same
   near-white the frosted pane resolves to (and the no-backdrop fallback uses).
   Dark sidebar text then reads on it exactly as it does on the desktop rail. */
:root[data-skin="premium"] [data-slot="sidebar"][data-mobile="true"] {
  background: var(--sidebar);
}

/* ---- 1. PAGE BACKGROUND — faint navy gradient wash, viewport-fixed ------- */
:root[data-skin="premium"] body {
  background:
    radial-gradient(1250px 640px at 84% -10%, color-mix(in srgb, var(--sky) 20%, transparent), transparent 62%),
    radial-gradient(900px 560px at 8% 6%, color-mix(in srgb, var(--gold) 13%, transparent), transparent 58%),
    radial-gradient(1050px 660px at -6% 100%, color-mix(in srgb, var(--brand) 11%, transparent), transparent 58%),
    radial-gradient(820px 520px at 100% 92%, color-mix(in srgb, var(--sky) 10%, transparent), transparent 60%),
    linear-gradient(180deg, #fcfdfe 0%, #f0f5fa 100%);
  background-attachment: fixed;
}

/* Canvas, dark. Same four radials, lower alpha over a near-black base so the
   wash reads without lifting the ground. */
:root[data-skin="premium"].dark body {
  background:
    radial-gradient(1250px 640px at 84% -10%, rgba(27, 161, 226, 0.12), transparent 62%),
    radial-gradient(900px 560px at 8% 6%, rgba(201, 162, 75, 0.07), transparent 58%),
    radial-gradient(1050px 660px at -6% 100%, rgba(95, 163, 220, 0.08), transparent 58%),
    radial-gradient(820px 520px at 100% 92%, rgba(27, 161, 226, 0.06), transparent 60%),
    linear-gradient(180deg, #0b1016 0%, #0e141d 100%);
  background-attachment: fixed;
}
/* let the painted canvas show through so white cards visibly float on it */
:root[data-skin="premium"] [data-slot="sidebar-inset"] {
  background: transparent;
}
:root[data-skin="premium"] ::selection {
  background: color-mix(in oklab, var(--sky) 22%, transparent);
}

/* ==========================================================================
   2. SIDEBAR — deep navy panel, light text, gold-tipped active pill
   ========================================================================== */
/* Painted navy gradient panel — DESKTOP ONLY. On the desktop rail the frosted
   ::after overlay above sits on top of this navy, so the rail reads LIGHT with
   dark text. The mobile Sheet (data-slot="sidebar") never gets that overlay, so
   this navy showed through raw and the rail's DARK text sat on it unreadable —
   the "blue sidebar on mobile" bug. The mobile drawer takes the solid light
   fill above instead, matching the rail's visible (frosted) surface. */
:root[data-skin="premium"] [data-slot="sidebar-inner"] {
  background: linear-gradient(180deg, #123a5e 0%, #10314f 46%, #0d2843 100%);
  box-shadow: inset -1px 0 0 rgba(16, 49, 79, 0.06);
}
/* ...and DESKTOP-ONLY is not the whole condition: it is GLASS-only. Both layers
   that cover this navy (the ::before mesh and the ::after frost) are gated on
   [data-surface="glass"], so in FLAT the navy is not an under-layer — it IS the
   rail, wearing the light rail's dark --sidebar-foreground (#1b2c3b) at
   1.05:1. That is the unreadable flat sidebar reported on prod 2026-08-19; it
   is the same failure the comment above describes for the mobile Sheet, which
   was fixed there and not here. Under glass this changes nothing: the rule
   above already sets the rail transparent at higher specificity, so the navy
   never painted in glass at all. Flat takes the same opaque light fill the
   frost resolves to, which is what the mobile drawer was given. */
:root[data-skin="premium"]:not([data-surface="glass"]) [data-slot="sidebar-inner"] {
  background: var(--sidebar);
}
:root[data-skin="premium"] [data-slot="sidebar-container"] {
  box-shadow: 1px 0 24px -18px rgba(10, 28, 46, 0.7);
}

/* Header + footer relight — these use GLOBAL text-muted-foreground /
   hover:text-foreground / font-semibold, which would be dark-on-navy. */
:root[data-skin="premium"] [data-slot="sidebar-header"] {
  color: var(--sidebar-foreground);
}
:root[data-skin="premium"] [data-slot="sidebar-header"] .font-semibold {
  /* was a hardcoded #10314f: correct on the light rail, 1.09:1 on the dark one
     (measured from pixels, not computed style). The token flips with the mode. */
  color: var(--sidebar-foreground);
}
:root[data-skin="premium"] [data-slot="sidebar-header"] .text-muted-foreground,
:root[data-skin="premium"] [data-slot="sidebar-footer"] .text-muted-foreground {
  color: color-mix(in oklab, var(--sidebar-foreground) 78%, transparent);
}
:root[data-skin="premium"] [data-slot="sidebar-header"] .hover\\:text-foreground:hover,
:root[data-skin="premium"] [data-slot="sidebar-footer"] .hover\\:text-foreground:hover {
  color: var(--sidebar-foreground);
}
:root[data-skin="premium"] [data-slot="sidebar-footer"] {
  color: color-mix(in oklab, var(--sidebar-foreground) 80%, transparent);
  border-top: 1px solid rgba(16, 49, 79, 0.1);
}

/* Section labels ("Apps"/"Platform") — quiet uppercase steel */
:root[data-skin="premium"] [data-slot="sidebar-group-label"] {
  text-transform: uppercase;
  letter-spacing: 0.07em;
  font-size: 0.6875rem;
  font-weight: 600;
  /* 74%, not 58%: at 58% these 11px uppercase labels measured 3.54:1 on the
     rail. Uppercase + letter-spaced small text is already the hardest thing in
     the sidebar to read, so it gets the 4.5:1 body threshold, not the 3:1
     large-text one. */
  color: color-mix(in oklab, var(--sidebar-foreground) 74%, transparent);
}

/* Nav icons — mid navy on the frosted rail, white only on the active pill
   (out-specifies globals.css:223 [data-sidebar="menu-item"] svg) */
:root[data-skin="premium"] [data-sidebar="menu-item"] svg {
  color: #2a78bd;
  transition: color 0.15s ease;
}
:root[data-skin="premium"] [data-slot="sidebar-menu-button"]:hover svg {
  /* token, not #10314f: a dark-navy icon vanishes on the dark rail on hover */
  color: var(--sidebar-foreground);
}
/* active sits on the dark gradient pill, so it stays white */
:root[data-skin="premium"] [data-slot="sidebar-menu-button"][data-active] svg {
  color: var(--primary-foreground);
}

/* Nav item base + faint-wash hover */
:root[data-skin="premium"] [data-slot="sidebar-menu-button"] {
  color: var(--sidebar-foreground);
  border-radius: var(--radius-md);
  font-weight: 500;
  transition: background 0.15s ease, color 0.15s ease, box-shadow 0.15s ease;
}
:root[data-skin="premium"] [data-slot="sidebar-menu-button"]:hover {
  background: rgba(16, 49, 79, 0.06);
  color: #10314f;
}
/* The hover state had no dark twin. A navy tint and navy ink are right on the
   LIGHT rail and invisible on the dark one — hovering a nav item dropped it from
   ~11:1 to 1.11-1.17:1, i.e. the label disappeared under the cursor. Idle state
   was fine, which is why 412 static route-states never caught it: nothing in the
   sweep hovered anything. Tokens, not hex, so this cannot drift again. */
:root[data-skin="premium"].dark [data-slot="sidebar-menu-button"]:hover:not([data-active]) {
  background: var(--sidebar-accent);
  color: var(--sidebar-foreground);
}

/* Bright active state — sky→navy gradient pill, gold inset left tab, white text.
   Selector is [data-active], NOT [data-active="true"]: Base UI renders the flag as a
   valueless boolean attribute, so the ="true" form matched nothing and this pill never
   painted — the fallback Tailwind data-active:bg-sidebar-accent was doing the work. */
/* The light stop is #177eb2, not the brand sky #1ba1e2: white label text over
   the sky end measured 2.90:1 (3.58:1 averaged across the pill) against the
   4.5:1 a 14px label needs. Deepening only that stop keeps the sky->navy sweep
   and the gold edge intact while the whole run clears 4.5:1 — darkening the
   text instead would have lost the pill's contrast against the rail. */
/* The gradient now ends on var(--brand), which is a LIGHT blue in dark mode, so a
   hardcoded white label drops to 4.02:1 there (caught by the contrast gate after
   the token conversion — it had been 7.72:1). --primary-foreground is the token
   that already means "ink that reads on the brand": white in light, #0b1622 in
   dark. Using it makes the pill correct for ANY brand instead of for navy only. */
:root[data-skin="premium"] [data-slot="sidebar-menu-button"][data-active] {
  background: linear-gradient(135deg, color-mix(in srgb, var(--brand) 72%, white) 0%, var(--brand) 100%);
  color: var(--primary-foreground);
  font-weight: 600;
  box-shadow:
    inset 3px 0 0 0 var(--gold),
    0 4px 14px -6px color-mix(in srgb, var(--sky) 50%, transparent);
}

/* Sub-menu — left rule + hover/active follow the same language */
:root[data-skin="premium"] [data-slot="sidebar-menu-sub"] {
  border-color: rgba(16, 49, 79, 0.14);
}
:root[data-skin="premium"] [data-slot="sidebar-menu-sub-button"] {
  color: color-mix(in oklab, var(--sidebar-foreground) 88%, transparent);
}
:root[data-skin="premium"] [data-slot="sidebar-menu-sub-button"]:hover {
  background: rgba(16, 49, 79, 0.05);
  color: #10314f;
}
:root[data-skin="premium"].dark [data-slot="sidebar-menu-sub-button"]:hover:not([data-active]) {
  background: var(--sidebar-accent);
  color: var(--sidebar-foreground);
}
:root[data-skin="premium"] [data-slot="sidebar-menu-sub-button"][data-active] {
  background: color-mix(in srgb, var(--sky) 16%, transparent);
  color: #ffffff;
  box-shadow: inset 2px 0 0 var(--gold);
}

/* Count badge (inline bg-brand text-primary-foreground) would be navy-on-navy.
   Retint to sky — utility class needs !important to beat @layer utilities.
   Amber module-tag badges (bg-amber-*) are semantic and left untouched. */
/* Deep-navy ink, not white: white on sky measured 2.90:1 light / 2.07:1 dark,
   where a 11px count needs 4.5:1. Inking it navy gets 5.82:1 / 8.14:1 and keeps
   the badge bright — darkening the sky fill enough for white text (41% toward
   navy) would have dulled the one accent that makes a count read as unread. */
:root[data-skin="premium"] [data-slot="sidebar-menu-badge"].bg-brand {
  background: var(--sky) !important;
  color: #0b1e31 !important;
  font-weight: 700;
  box-shadow: 0 1px 2px rgba(10, 28, 46, 0.35);
}
/* Module "BETA" tag badges (bg-amber-100/text-amber-700) sit on the deep-navy rail —
   retint to a refined translucent gold-on-navy so they read as a premium accent, not
   a pale sticker. (Amber status badges on white cards elsewhere are left untouched.) */
/* The ink follows the rail. A pale gold sticker on a pale gold wash over a LIGHT
   rail measured 1.16:1 — the single widest contrast failure in the app, on 29
   routes in all four states, because the gold-on-navy treatment was designed for
   the dark rail only and the light rail inherited it unchanged. Dark ink on the
   same wash reads 13.26:1 in light; the dark rail keeps its gold at 6.95:1. */
:root[data-skin="premium"] [data-slot="sidebar-menu-badge"].bg-amber-100 {
  /* OPAQUE, not a 20% wash. A translucent badge takes whatever is behind it,
     and on an ACTIVE nav item that is the blue pill, not the rail — dark ink on
     the pill measured 2.91:1 while the same badge on the rail was fine. An
     opaque fill makes the badge independent of its backdrop: 7.62:1. */
  background: var(--gold) !important;
  color: #1b1405 !important;
  box-shadow: inset 0 0 0 1px color-mix(in oklab, var(--gold) 34%, transparent);
}
:root[data-skin="premium"].dark [data-slot="sidebar-menu-badge"].bg-amber-100 {
  /* Dark rail: a muted gold fill with pale gold ink, 4.94:1, so the tag reads as
     an accent rather than a bright sticker on the dark pane. */
  background: #6a6243 !important;
  color: #f5e6c4 !important;
}

/* Sidebar search variant sits on the navy rail */
:root[data-skin="premium"] [data-slot="sidebar-input"],
:root[data-skin="premium"] [data-sidebar="input"] {
  background: rgba(255, 255, 255, 0.09);
  border-color: rgba(255, 255, 255, 0.14);
  color: #ffffff;
}

/* ==========================================================================
   3. TOPBAR — frosted translucent white, navy hairline + soft shadow
   ========================================================================== */
:root[data-skin="premium"] [data-slot="topbar"] {
  background: rgba(247, 250, 253, 0.82);
  border-bottom: 1px solid color-mix(in srgb, var(--brand) 12%, transparent);
  backdrop-filter: blur(14px) saturate(1.4);
  -webkit-backdrop-filter: blur(14px) saturate(1.4);
  box-shadow:
    0 1px 0 color-mix(in srgb, var(--brand) 4%, transparent),
    0 10px 26px -22px color-mix(in srgb, var(--brand) 55%, transparent);
}

/* Topbar, dark. This was the single worst miss of the light-only skin: a
   near-white bar (rgba 247,250,253) under near-white dark-mode text, i.e. the
   first thing on screen was illegible. */
:root[data-skin="premium"].dark [data-slot="topbar"] {
  background: rgba(14, 20, 29, 0.82);
  border-bottom: 1px solid rgba(255, 255, 255, 0.10);
}
:root[data-skin="premium"] [data-slot="topbar"] .text-foreground {
  color: var(--foreground);
  letter-spacing: -0.01em;
}
:root[data-skin="premium"] [data-slot="topbar"] [data-slot="separator"] {
  background: color-mix(in srgb, var(--brand) 14%, transparent);
}
/* Avatar chip — navy on a light navy wash */
:root[data-skin="premium"] [data-slot="avatar-fallback"] {
  background: var(--brand-muted);
  color: var(--brand);
  font-weight: 600;
}

/* ==========================================================================
   4. CARDS — elevated floating panels over the gradient
   ========================================================================== */
:root[data-skin="premium"] [data-slot="card"],
:root[data-skin="premium"] .bg-card {
  background: var(--card);
  border: 1px solid color-mix(in srgb, var(--brand) 8%, transparent);
  border-radius: var(--radius-xl);
  box-shadow:
    0 1px 2px rgba(16, 42, 68, 0.05),
    0 14px 34px -18px rgba(16, 42, 68, 0.22);
  transition: box-shadow 0.2s ease;
}
:root[data-skin="premium"] [data-slot="card"]:hover {
  box-shadow:
    0 2px 4px rgba(16, 42, 68, 0.07),
    0 20px 46px -20px rgba(16, 42, 68, 0.3);
}

/* ==========================================================================
   5. BUTTONS — restyled via the concrete static variant classes
   ========================================================================== */
/* Primary — navy sheen gradient + lift */
:root[data-skin="premium"] [data-slot="button"].bg-primary {
  background-image: linear-gradient(180deg, #2a5f92 0%, var(--brand) 100%);
  color: var(--primary-foreground);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.14),
    0 1px 2px rgba(16, 42, 68, 0.22),
    0 8px 18px -10px color-mix(in srgb, var(--brand) 55%, transparent);
  transition: background-image 0.15s ease, box-shadow 0.15s ease, transform 0.05s ease;
}
:root[data-skin="premium"] [data-slot="button"].bg-primary:hover {
  background-image: linear-gradient(180deg, #316aa0 0%, #235688 100%);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.16),
    0 1px 2px rgba(16, 42, 68, 0.24),
    0 12px 24px -10px color-mix(in srgb, var(--brand) 65%, transparent);
}

/* Primary button, dark. The gradient is navy and dark mode sets
   --primary-foreground to near-black, so the label measured 2.1:1 on it. Lift
   the gradient to the dark --primary family: the same near-black label then
   reads at 6.7:1. */
:root[data-skin="premium"].dark [data-slot="button"].bg-primary {
  background-image: linear-gradient(180deg, #6fb0e4 0%, var(--brand) 100%);
  color: var(--primary-foreground);
}
:root[data-skin="premium"].dark [data-slot="button"].bg-primary:hover {
  background-image: linear-gradient(180deg, #7ec0f0 0%, #6fb0e4 100%);
}
:root[data-skin="premium"] [data-slot="button"].bg-primary:active {
  transform: translateY(0.5px);
}
/* Outline / default — clean white chip with sky-tinted hover edge.
   Target both the bg-background and border-border hooks the variant emits. */
:root[data-skin="premium"] [data-slot="button"].bg-background,
:root[data-skin="premium"] [data-slot="button"].border-border {
  background: var(--card);
  border-color: var(--border);
  box-shadow: 0 1px 2px rgba(16, 42, 68, 0.05);
}
:root[data-skin="premium"] [data-slot="button"].bg-background:hover,
:root[data-skin="premium"] [data-slot="button"].border-border:hover {
  background: var(--muted);
  border-color: color-mix(in oklab, var(--sky) 35%, var(--border));
}

/* ==========================================================================
   6. INPUTS
   ========================================================================== */
:root[data-skin="premium"] [data-slot="input"] {
  background: var(--card);
  border-color: var(--input);
  border-radius: var(--radius-md);
  box-shadow: inset 0 1px 2px rgba(16, 42, 68, 0.04);
}
:root[data-skin="premium"] [data-slot="input"]:focus-visible {
  border-color: var(--sky);
  box-shadow: 0 0 0 3px color-mix(in oklab, var(--sky) 20%, transparent);
}

/* Dark islands.

   Everything above is written for the light app shell and hard-codes white
   surfaces — which beats whatever --background a .dark subtree sets,
   because a :root-scoped rule knows nothing about the subtree. Several pages
   are deliberately dark islands: the invite page, the auth pages and the whole
   onboarding flow all force .dark so the gradient field reads.

   The result on /invite was an outline button rendering near-white text on
   #ffffff — measured contrast 1.05:1, i.e. invisible. "I already have an
   account" is the entire path for an existing user being added to a second
   workspace, and it could not be read.

   These restore the token inside those subtrees. Adding .dark also raises
   specificity, so they win over the light rules without !important. */
:root[data-skin="premium"] .dark [data-slot="button"].bg-background,
:root[data-skin="premium"] .dark [data-slot="button"].border-border {
  background: color-mix(in oklab, var(--foreground) 8%, transparent);
  border-color: var(--border);
  box-shadow: none;
}
:root[data-skin="premium"] .dark [data-slot="button"].bg-background:hover,
:root[data-skin="premium"] .dark [data-slot="button"].border-border:hover {
  background: color-mix(in oklab, var(--foreground) 14%, transparent);
  border-color: color-mix(in oklab, var(--sky) 35%, var(--border));
}
:root[data-skin="premium"] .dark [data-slot="input"] {
  background: color-mix(in oklab, var(--foreground) 6%, transparent);
  border-color: var(--border);
  box-shadow: none;
}

/* ==========================================================================
   7. TABLES / LISTS
   ========================================================================== */
:root[data-skin="premium"] [data-slot="table-container"] {
  border-radius: var(--radius-lg);
}
:root[data-skin="premium"] [data-slot="table-header"] {
  background: color-mix(in srgb, var(--brand) 4%, transparent);
}
:root[data-skin="premium"] [data-slot="table-head"] {
  color: var(--secondary-foreground);
  font-weight: 600;
  letter-spacing: 0.02em;
}
:root[data-skin="premium"] [data-slot="table-row"] {
  border-color: var(--border);
  transition: background-color 0.12s ease;
}
:root[data-skin="premium"] [data-slot="table-row"]:hover {
  background: color-mix(in oklab, var(--sky) 6%, transparent);
}
:root[data-skin="premium"] [data-slot="table-row"][data-state="selected"] {
  background: color-mix(in oklab, var(--brand) 8%, transparent);
}
:root[data-skin="premium"] [data-slot="table-footer"] {
  background: color-mix(in srgb, var(--brand) 4%, transparent);
  border-color: var(--border);
}

/* ==========================================================================
   8. DROPDOWN / MENUS — soft floating popover with layered navy shadow
   ========================================================================== */
:root[data-skin="premium"] [data-slot="dropdown-menu-content"],
:root[data-skin="premium"] .bg-popover {
  background: var(--popover);
  border: 1px solid color-mix(in srgb, var(--brand) 10%, transparent);
  border-radius: var(--radius-lg);
  box-shadow:
    0 2px 6px rgba(16, 42, 68, 0.06),
    0 20px 46px -16px rgba(16, 42, 68, 0.3);
}
:root[data-skin="premium"] [data-slot="dropdown-menu-item"] {
  border-radius: var(--radius-sm);
}
:root[data-skin="premium"] [data-slot="dropdown-menu-item"]:focus,
:root[data-skin="premium"] [data-slot="dropdown-menu-item"][data-highlighted] {
  background: var(--accent);
  color: var(--accent-foreground);
}
:root[data-skin="premium"] [data-slot="dropdown-menu-label"] {
  color: var(--muted-foreground);
  font-weight: 600;
}
:root[data-skin="premium"] [data-slot="dropdown-menu-separator"] {
  background: var(--border);
}

/* ==========================================================================
   9. BADGES / PILLS
   ========================================================================== */
:root[data-skin="premium"] [data-slot="badge"].bg-primary {
  background: var(--brand);
  color: var(--brand-foreground);
  box-shadow: 0 1px 2px color-mix(in srgb, var(--brand) 20%, transparent);
}
:root[data-skin="premium"] [data-slot="badge"].border-border {
  border-color: color-mix(in oklab, var(--border) 90%, transparent);
  color: var(--secondary-foreground);
}`,
};

/**
 * The designed chrome — sidebar rail, topbar, pills, badges — re-scoped to ANY
 * brand id.
 *
 * This sheet used to be reachable only as SHELL_SKIN_CSS["premium"], which is
 * why a whitelabel had to choose between its own colours and a designed shell:
 * authoring a themeConfig re-ids the brand to `wl-<id>` (resolve.ts), and there
 * was no entry under that key, so it got NO chrome at all. Now that every brand
 * colour in the sheet is a token rather than baked navy, the same rules render
 * correctly in any palette — so the only thing that had to change is the scope.
 *
 * Re-scoping at runtime rather than templating the 83 selectors keeps the
 * premium output BYTE-IDENTICAL (the replace is a no-op for id "premium"), which
 * is what lets the pixel test prove nothing regressed.
 */
/**
 * The FLAT chrome — see the sheet's own header for why it exists and exactly
 * which premium decision each rule inverts. Authored against
 * `data-skin="premium"` like its sibling purely so the two share one scoping
 * mechanism; the id is rewritten to the caller's brand below.
 */
export const FLAT_CHROME_CSS = String.raw`
/* ═══════════════════════════════════════════════════════════════════════════
   "FLAT" CHROME — a deliberately DIFFERENT shell, not the premium look in
   another palette.

   The premium chrome is frosted, rounded, airy and shadowed: a glass rail over
   a colour mesh, pill-shaped nav items, soft cards floating on a gradient wash.
   Recolouring it produces a product that still reads as the house ERP, which is
   the exact failure a whitelabel exists to avoid.

   This one inverts every one of those decisions, so the two cannot be mistaken
   for each other even at a glance:

     premium                        flat
     ────────────────────────────   ────────────────────────────────────────
     frosted glass rail + mesh      solid rail, hard 1px edge, no blur
     pill active state              square block + 3px leading accent bar
     soft shadows, no borders       1px borders, zero shadow
     rounded 10-16px                4px
     airy rows                      dense rows, tabular rhythm
     sentence-case labels           uppercase, letterspaced section labels
     gradient page wash             flat single-colour field

   Every colour still comes from the brand's own tokens — this file styles
   STRUCTURE, never palette. */

/* ---- RAIL ---------------------------------------------------------------- */
:root[data-skin="premium"] [data-slot="sidebar-inner"] {
  position: relative;
  isolation: isolate;
  background: var(--sidebar);
}
/* No mesh, no frosted pane: the two pseudo-element layers the premium rail is
   built from are removed outright rather than recoloured. */
:root[data-skin="premium"] [data-slot="sidebar-inner"]::before,
:root[data-skin="premium"] [data-slot="sidebar-inner"]::after { content: none; }
:root[data-skin="premium"] [data-slot="sidebar-container"] {
  border-right: 1px solid color-mix(in srgb, var(--sidebar-foreground) 16%, transparent);
}
:root[data-skin="premium"] [data-slot="sidebar"][data-mobile="true"] { background: var(--sidebar); }

/* Section labels read as machinery, not chat. */
:root[data-skin="premium"] [data-slot="sidebar-group-label"] {
  text-transform: uppercase;
  letter-spacing: .12em;
  font-size: 10.5px;
  font-weight: 800;
  opacity: .62;
}

/* Square block with a leading accent bar — the single most recognisable
   difference from a pill, and it reads at thumbnail size. */
:root[data-skin="premium"] [data-slot="sidebar-menu-button"] {
  border-radius: 0;
  padding-inline: 14px 10px;
  border-left: 3px solid transparent;
  font-weight: 600;
}
:root[data-skin="premium"] [data-slot="sidebar-menu-button"]:hover {
  background: color-mix(in srgb, var(--sidebar-foreground) 8%, transparent);
}
:root[data-skin="premium"] [data-slot="sidebar-menu-button"][data-active="true"] {
  background: color-mix(in srgb, var(--sidebar-foreground) 13%, transparent);
  border-left-color: var(--gold, var(--brand));
  box-shadow: none;
}

/* ---- TOP BAR ------------------------------------------------------------- */
:root[data-skin="premium"] [data-slot="topbar"] {
  background: var(--card);
  border-bottom: 2px solid var(--border);
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  box-shadow: none;
}

/* ---- SURFACES: bordered, never shadowed ---------------------------------- */
:root[data-skin="premium"] [data-slot="card"],
:root[data-skin="premium"] .bg-card,
:root[data-skin="premium"] [data-slot="popover-content"],
:root[data-skin="premium"] [data-slot="dropdown-menu-content"] {
  border: 1px solid var(--border);
  border-radius: 4px;
  box-shadow: none;
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
}

/* ---- CONTROLS ------------------------------------------------------------ */
:root[data-skin="premium"] button,
:root[data-skin="premium"] [data-slot="button"] {
  border-radius: 4px;
  box-shadow: none;
  background-image: none;
  font-weight: 650;
  letter-spacing: .01em;
}
:root[data-skin="premium"] input,
:root[data-skin="premium"] textarea,
:root[data-skin="premium"] select,
:root[data-skin="premium"] [data-slot="input"] {
  border-radius: 4px;
  box-shadow: none;
}

/* ---- FIELD: flat, no gradient wash --------------------------------------- */
:root[data-skin="premium"] body { background: var(--background); }
:root[data-skin="premium"] [data-slot="sidebar-inset"] { background: var(--background); }

/* ---- THE REMAINING TELLS -------------------------------------------------
   Structure alone was not enough: with the rail and cards changed, what still
   read as the house product was the topbar furniture — a pill-shaped command
   field with a soft ring, a circular avatar, and a thin borderless header. Those
   are the three things a returning QOD user recognises before they read a word,
   so they get squared and weighted too. */
:root[data-skin="premium"] [data-slot="topbar"] {
  height: 3.25rem;
}
/* The command field: a rectangle with a hard border, not a floating pill. */
:root[data-skin="premium"] [data-slot="topbar"] [data-slot="input"],
:root[data-skin="premium"] [data-slot="topbar"] input,
:root[data-skin="premium"] [data-slot="command-trigger"],
:root[data-skin="premium"] [data-slot="topbar"] button[class*="rounded-full"] {
  border-radius: 3px;
}
/* Square the avatar. A circle is the single most "consumer app" cue in the
   chrome; a field-service console reads as a badge, not a profile picture. */
:root[data-skin="premium"] [data-slot="avatar"],
:root[data-skin="premium"] [data-slot="avatar"] img,
:root[data-skin="premium"] [data-slot="avatar-fallback"] {
  border-radius: 3px;
}
/* Page titles in the brand's display face, uppercase and tight — the house
   product sets them sentence-case in the body face. */
:root[data-skin="premium"] [data-slot="topbar"] h1,
:root[data-skin="premium"] [data-slot="topbar"] [data-slot="topbar-title"] {
  font-family: var(--brand-font-heading, var(--font-sans));
  text-transform: uppercase;
  letter-spacing: .06em;
  font-size: 14px;
  font-weight: 700;
}
/* Brand lockup in the rail head, same treatment. */
:root[data-skin="premium"] [data-slot="sidebar-header"] {
  border-bottom: 1px solid color-mix(in srgb, var(--sidebar-foreground) 14%, transparent);
}

/* ---- DENSITY: tabular rhythm --------------------------------------------- */
:root[data-skin="premium"] [data-slot="table"] td,
:root[data-skin="premium"] [data-slot="table"] th {
  padding-block: 8px;
  border-bottom: 1px solid var(--border);
}
:root[data-skin="premium"] [data-slot="table"] th {
  text-transform: uppercase;
  letter-spacing: .08em;
  font-size: 11px;
  font-weight: 800;
}
`;

export const MINIMAL_CHROME_CSS = String.raw`
/* ═══════════════════════════════════════════════════════════════════════════
   "MINIMAL" CHROME — the third design language.

   Two looks is a toggle, not a choice. Where "designed" separates surfaces with
   frost and shadow and "flat" separates them with borders, this one separates
   them with NOTHING but space and weight — no card outlines, no fills on nav,
   no shadows anywhere. It suits a brand that wants the product to feel like a
   document rather than an instrument.

   Structure only; every colour comes from the brand's own tokens. */

/* ---- RAIL: no fills, no chrome, weight does the work --------------------- */
:root[data-skin="premium"] [data-slot="sidebar-inner"] {
  position: relative;
  background: var(--sidebar);
}
:root[data-skin="premium"] [data-slot="sidebar-inner"]::before,
:root[data-skin="premium"] [data-slot="sidebar-inner"]::after { content: none; }
:root[data-skin="premium"] [data-slot="sidebar-container"] { border-right: 0; }
:root[data-skin="premium"] [data-slot="sidebar"][data-mobile="true"] { background: var(--sidebar); }
:root[data-skin="premium"] [data-slot="sidebar-group-label"] {
  text-transform: none;
  letter-spacing: 0;
  font-size: 12px;
  font-weight: 500;
  opacity: .5;
}
/* Active is a WEIGHT change, not a container. */
:root[data-skin="premium"] [data-slot="sidebar-menu-button"] {
  border-radius: 6px;
  font-weight: 450;
  background: transparent;
}
:root[data-skin="premium"] [data-slot="sidebar-menu-button"]:hover {
  background: color-mix(in srgb, var(--sidebar-foreground) 6%, transparent);
}
:root[data-skin="premium"] [data-slot="sidebar-menu-button"][data-active="true"] {
  background: transparent;
  font-weight: 700;
  box-shadow: none;
}

/* ---- TOP BAR: a hairline, nothing else ---------------------------------- */
:root[data-skin="premium"] [data-slot="topbar"] {
  background: transparent;
  border-bottom: 1px solid color-mix(in srgb, var(--border) 60%, transparent);
  backdrop-filter: none;
  -webkit-backdrop-filter: none;
  box-shadow: none;
}

/* ---- SURFACES: no outline at all ---------------------------------------- */
:root[data-skin="premium"] [data-slot="card"],
:root[data-skin="premium"] .bg-card {
  border: 0;
  box-shadow: none;
  background: transparent;
}
/* Popovers DO need an edge — they float over content and would otherwise have
   no boundary at all. The one place this look keeps a border. */
:root[data-skin="premium"] [data-slot="popover-content"],
:root[data-skin="premium"] [data-slot="dropdown-menu-content"] {
  border: 1px solid var(--border);
  box-shadow: 0 8px 30px rgba(0,0,0,.08);
}

/* ---- CONTROLS: quiet until touched -------------------------------------- */
:root[data-skin="premium"] button,
:root[data-skin="premium"] [data-slot="button"] {
  border-radius: 6px;
  box-shadow: none;
  background-image: none;
}
:root[data-skin="premium"] input,
:root[data-skin="premium"] textarea,
:root[data-skin="premium"] [data-slot="input"] {
  border-radius: 6px;
  box-shadow: none;
  background: color-mix(in srgb, var(--foreground) 4%, transparent);
  border-color: transparent;
}
:root[data-skin="premium"] input:focus-visible,
:root[data-skin="premium"] [data-slot="input"]:focus-visible { border-color: var(--ring); }

/* ---- AIR ---------------------------------------------------------------- */
:root[data-skin="premium"] body { background: var(--background); }
:root[data-skin="premium"] [data-slot="sidebar-inset"] { background: var(--background); }
:root[data-skin="premium"] [data-slot="table"] td,
:root[data-skin="premium"] [data-slot="table"] th {
  padding-block: 14px;
  border-bottom: 1px solid color-mix(in srgb, var(--border) 50%, transparent);
}
`;

export function minimalChromeCss(scopeId: string): string {
  if (!/^[A-Za-z0-9_-]+$/.test(scopeId)) return "";
  return MINIMAL_CHROME_CSS.replaceAll('data-skin="premium"', `data-skin="${scopeId}"`);
}

export function flatChromeCss(scopeId: string): string {
  if (!/^[A-Za-z0-9_-]+$/.test(scopeId)) return "";
  return FLAT_CHROME_CSS.replaceAll('data-skin="premium"', `data-skin="${scopeId}"`);
}

export function shellChromeCss(scopeId: string, opts?: { withPalette?: boolean }): string {
  // A brand id reaches CSS as an attribute value; keep it to what a skin id can be.
  if (!/^[A-Za-z0-9_-]+$/.test(scopeId)) return "";
  const sheet = SHELL_SKIN_CSS.premium;

  // The sheet opens with premium's OWN palette (the `0. DESIGN TOKENS` blocks) and
  // then styles components. For premium that palette is the point. For any other
  // brand it is poison: buildSkinCss emits that brand's tokens FIRST, and these
  // blocks are appended after at higher specificity (`:not(.dark)`), so they win —
  // a custom brand rendered every --brand declaration in order as
  // ["#6d28d9", "#1f4e79", "#65a7dd"] and wore premium's navy. The chrome reached
  // it; its colours did not. Dropping the palette leaves the component rules, all
  // of which now read var(--brand)/var(--sky)/var(--gold) — so they render in
  // whatever palette the brand supplied.
  const CHROME_STARTS_AT = "/* ---- SIDEBAR GLASS";
  const cut = sheet.indexOf(CHROME_STARTS_AT);
  const withPalette = opts?.withPalette ?? scopeId === "premium";
  const body = withPalette || cut < 0 ? sheet : sheet.slice(cut);

  return body.replaceAll('data-skin="premium"', `data-skin="${scopeId}"`);
}

/**
 * Skins whose tokens are pinned the same under `.dark` as under light — for
 * these, the topbar's light/dark toggle changes the `<html>` class and swaps
 * its own Sun/Moon icon, but nothing on the page actually looks different.
 * Left as an explicit opt-out list, not "every id not in SHELL_SKIN_CSS", so
 * a future skin has to be added here deliberately once it's confirmed
 * dark-safe (i.e. confirmed its .dark/non-.dark tokens really are identical),
 * rather than silently inheriting a broken toggle it was never designed for.
 *
 * The 6 shipped SKINS (skins.ts) each declare a single `mode: "light"` — no
 * per-mode variant field exists on ThemeConfig, and skinVarDeclarations
 * (build-skin-css.ts) never branches on mode, so its output is IDENTICAL for
 * every one of them under `.dark`. "premium" is the one exception: it has its
 * own hand-authored, contrast-verified `.dark` twin right here in
 * SHELL_SKIN_CSS (see the "0. DESIGN TOKENS" comment above — "They used to be
 * emitted under the .dark selector too... dark mode never applied for any
 * skinned org, and premium is the platform default [ssr-skin.ts]. The dark
 * twin follows."), appended after skinVarDeclarations at higher specificity,
 * so premium's dark mode genuinely works. This set previously listed
 * "premium" — backwards from the fix its own neighbouring comment describes,
 * and since premium is the platform default, that meant dark mode was force-
 * disabled (via the correcting effect below) for most users on the product,
 * while the 5 skins that truly don't differentiate were never flagged.
 */
export const SKINS_WITHOUT_DARK_MODE = new Set<string>(["default", "glass", "handyman", "sleek", "fresh"]);
